// Filmpjes in de hero van de home (module site). Gebundeld naar /js/site/hero.js.
//
// De hero heeft drie filmpjes naast elkaar (O-50): Installeren, Bouwen en Demo, in een rij die
// horizontaal scrolt (.hero-rij, CSS scroll-snap). Bouwen (.hero-vak-start) hoort bij het laden in
// het midden te staan. Browsers met scroll-initial-target doen dat zelf. Staat de rij nog aan het
// begin, dan zet dit script hem meteen goed, zonder animatie.
//
// Onder de rij staan drie streepjes (.hero-dots > .hero-dot, Q5-O1), één per filmpje. De rij is een
// carrousel (Q7-O1): het vak in het midden is groot, de buren zijn kleiner (site.css). Dit script
// zet .is-actief op het vak en het streepje dat het dichtst bij het midden van de rij staan. Dat
// rekent het uit de scrollpositie en de plek van de vakken in de opmaak, niet uit wat er te zien is,
// omdat het krimpen van de buren anders meetelt. Een klik op een buurvak of een streepje scrolt dat
// vak naar het midden, zonder animatie bij prefers-reduced-motion. Zonder JavaScript staan Bouwen
// en zijn streepje groot (de serverkant zet die classes al op de HTML).
//
// Alleen het gekozen filmpje speelt (Q6-O1). De HTML heeft geen autoplay en overal preload="none",
// dus zonder dit script laadt er geen filmpje. Dit script laadt en speelt het filmpje van het actieve vak.
// Wordt een ander vak actief, dan gaat het vorige terug naar zijn poster (preload="none" en load(),
// zodat het niets meer laadt) en laadt en speelt het nieuwe. Staat de hero buiten beeld of is het
// tabblad verborgen, dan gaat het filmpje terug naar zijn poster, zodat het laden stopt, en speelt
// het verder waar het was als je terugkomt (Q9-O2). Wie in het
// systeem minder beweging vraagt (prefers-reduced-motion: reduce), krijgt alleen de posters.
// Verandert die instelling terwijl de pagina open is, dan volgen de filmpjes. Zonder JavaScript
// staan de posters stil.
//
// Q9-O1: onder de rij staat één label (.hero-label, aria-live) met de naam van het gekozen filmpje.
// Dit script zet die naam als het scrollen stilstaat, en aria-current op het actieve streepje. Op een
// smal scherm (onder 768 px) laadt het de mobiele versie van Installeren en Bouwen (data-mobiel op
// de video), die inzoomt zodat de tekst te lezen is. Het kiest één keer, bij het laden. De poster
// kiest de browser met <picture>; dit script zet die keuze daarna vast.
//
// D8-O1: een video met data-snelheid speelt op die snelheid (Installeren: 0,75), ook na load() en
// na de wissel naar de mobiele bron. De gif als terugval blijft even snel.
//
// Afspraak met de HTML van paginas.ts: .hero-rij > .hero-vak[data-hero-vak][data-hero-label] >
// .media-kaart-beeld > video[data-mobiel][data-snelheid] > source, .hero-label en .hero-dots > .hero-dot[data-hero-dot].

const heroRij = document.querySelector<HTMLElement>(".hero-rij");
const heroStart = heroRij?.querySelector<HTMLElement>(".hero-vak-start");
const vakken = [...document.querySelectorAll<HTMLElement>(".hero-vak[data-hero-vak]")];
const stippen = [...document.querySelectorAll<HTMLButtonElement>(".hero-dot[data-hero-dot]")];
const label = document.querySelector<HTMLElement>(".hero-label");
const minderBeweging = window.matchMedia("(prefers-reduced-motion: reduce)");

// Gelijk aan HERO_MOBIEL_MEDIA in paginas.ts en de mediaquery in site.css. Eén keer bij het laden.
const mobiel = window.matchMedia("(max-width: 767px)").matches;

// Q9-O2: de browser koos bij het lezen van de HTML met <picture> de mobiele of de gewone poster. Draai
// je de telefoon over 768 px heen, dan zou hij de andere ook laden. Daarom ligt de keuze hier vast,
// net als bij de video: op een smal scherm geldt de mobiele poster altijd, anders valt hij weg. De
// gekozen poster verandert daardoor niet, dus er laadt niets extra.
for (const bron of document.querySelectorAll<HTMLSourceElement>(".hero-vak picture > source[media]")) {
  if (mobiel) bron.media = "all";
  else bron.remove();
}

/**
 * Op een smal scherm de mobiele versie als bron, vlak voordat het filmpje gaat spelen. De browser
 * koos zijn bron al bij het lezen van de HTML; load() laat hem opnieuw kiezen. Dat gebeurt pas hier,
 * omdat load() ook met preload="none" al een stukje ophaalt.
 */
function kiesBron(video: HTMLVideoElement): void {
  const bron = video.querySelector("source");
  const doel = video.dataset.mobiel;
  if (!mobiel || !bron || !doel || bron.getAttribute("src") === doel) return;
  bron.setAttribute("src", doel);
  video.load();
}

/**
 * Heeft de bezoeker de rij zelf bewogen (vegen, scrollwiel, toetsen, een klik op een vak of streepje)?
 * Tot dan hoort Bouwen in het midden. Safari op de iPhone kent scroll-initial-target niet, en zet een
 * rij met scroll-snap na het laden soms terug op het eerste vak. Zonder deze vlag telde die sprong als
 * een veeg, en speelde Installeren in plaats van Bouwen (Stijn 01-10).
 */
let zelfBewogen = false;

/** Bouwen in het midden, zolang de bezoeker de rij niet zelf heeft bewogen. */
function centreer(): void {
  if (!heroRij || !heroStart || zelfBewogen) return;
  // offsetLeft telt vanaf .hero-rij (position: relative). De rij zelf heeft geen smooth scrolling.
  const doel = heroStart.offsetLeft + heroStart.offsetWidth / 2 - heroRij.clientWidth / 2;
  if (Math.abs(heroRij.scrollLeft - doel) > 1) heroRij.scrollLeft = doel;
}

if (heroRij) {
  for (const soort of ["pointerdown", "touchstart", "wheel", "keydown"] as const) {
    heroRij.addEventListener(soort, () => (zelfBewogen = true), { passive: true });
  }
}
centreer();
// Na het laden van lettertypen en beelden kan de opmaak verschuiven, en Safari zet de snap dan opnieuw.
requestAnimationFrame(centreer);
window.addEventListener("load", centreer);
window.addEventListener("resize", centreer);

/** Het vak dat het meest in beeld is. Bij het laden Bouwen. */
let actief = heroStart?.dataset.heroVak ?? vakken[0]?.dataset.heroVak ?? "";
/** Staat de rij met filmpjes (deels) in het venster? Onbekend tot de eerste melding: dan niet. */
let heroInBeeld = false;

function videoVan(vak: HTMLElement): HTMLVideoElement | null {
  return vak.querySelector("video");
}

/**
 * D8-O1: afspeelsnelheid uit data-snelheid (Installeren: 0,75). load() zet playbackRate terug op
 * defaultPlaybackRate, dus die zetten we ook. Voor de zekerheid gebeurt het bij elke loadstart en
 * vlak voor elke play(), zodat een wissel tussen de mobiele en de gewone bron de snelheid houdt.
 */
function zetSnelheid(video: HTMLVideoElement): void {
  const snelheid = Number(video.dataset.snelheid);
  if (!(snelheid > 0) || snelheid === 1) return;
  video.defaultPlaybackRate = snelheid;
  if (video.playbackRate !== snelheid) video.playbackRate = snelheid;
}

/** Terug naar de poster: stoppen en niets meer laden. De video wordt dan doorzichtig en de <picture> eronder is weer te zien. */
function naarPoster(video: HTMLVideoElement): void {
  video.classList.remove("speelt");
  if (video.paused && video.currentTime === 0) return;
  video.pause();
  video.preload = "none";
  video.load();
}

/**
 * Waar het gekozen filmpje was toen de hero uit beeld ging of het tabblad verborgen werd (Q9-O2).
 * Komt hij terug, dan speelt het filmpje daar verder.
 */
const hervatVanaf = new WeakMap<HTMLVideoElement, number>();

/** Speelt alleen het filmpje van het actieve vak, en alleen als dat mag en te zien is. */
function volg(): void {
  const spelen = heroInBeeld && document.visibilityState === "visible" && !minderBeweging.matches;
  for (const vak of vakken) {
    const video = videoVan(vak);
    if (!video) continue;
    if (vak.dataset.heroVak !== actief || minderBeweging.matches) {
      hervatVanaf.delete(video);
      naarPoster(video);
    } else if (!spelen) {
      // Q9-O2: alleen pauzeren liet de browser met preload="auto" het hele filmpje doorladen (Demo:
      // 2,5 MB nadat de hero uit beeld was). Daarom terug naar de poster, zodat het laden stopt, en de
      // plek onthouden.
      if (!video.paused || video.currentTime > 0) hervatVanaf.set(video, video.currentTime);
      naarPoster(video);
    } else if (video.paused) {
      kiesBron(video);
      video.preload = "auto";
      // Na load() geldt currentTime als beginpunt zodra de browser weet hoe het filmpje in elkaar zit.
      const vanaf = hervatVanaf.get(video);
      hervatVanaf.delete(video);
      if (vanaf) video.currentTime = vanaf;
      zetSnelheid(video);
      video.play().catch(() => {
        // Afspelen kan geweigerd worden (bijvoorbeeld energiebesparing); de poster blijft dan staan.
      });
    }
  }
}

/**
 * Q9-O2: het label (aria-live) krijgt de nieuwe naam pas als het scrollen stilstaat, zodat een
 * schermlezer bij een veeg over twee filmpjes heen alleen het laatste noemt.
 */
function zetLabel(): void {
  const tekst = vakken.find((v) => v.dataset.heroVak === actief)?.dataset.heroLabel;
  if (label && tekst && label.textContent !== tekst) label.textContent = tekst;
}

function zetActief(naam: string): void {
  for (const stip of stippen) {
    const aan = stip.dataset.heroDot === naam;
    stip.classList.toggle("is-actief", aan);
    if (aan) stip.setAttribute("aria-current", "true");
    else stip.removeAttribute("aria-current");
  }
  for (const vak of vakken) vak.classList.toggle("is-actief", vak.dataset.heroVak === naam);
  if (naam === actief) return;
  actief = naam;
  volg();
}

// Q9-O1: de video heeft geen poster; de <picture> eronder is de poster. Een geladen video toont zijn
// eerste beeld, ook na load(). Daarom is hij onzichtbaar (site.css) tot hij echt speelt.
for (const vak of vakken) {
  const video = videoVan(vak);
  if (!video) continue;
  zetSnelheid(video);
  video.addEventListener("loadstart", () => zetSnelheid(video));
  video.addEventListener("playing", () => {
    if (vak.dataset.heroVak === actief) video.classList.add("speelt");
  });
}

minderBeweging.addEventListener("change", volg);
document.addEventListener("visibilitychange", volg);

if (heroRij) {
  new IntersectionObserver((entries) => {
    heroInBeeld = entries.some((e) => e.isIntersecting);
    volg();
  }).observe(heroRij);
}

// ---------------------------------------------------------------- streepjes en actief vak

if (heroRij && vakken.length) {
  const rij = heroRij;
  /** Het vak waarvan het midden het dichtst bij het midden van de rij staat. offsetLeft telt de scale niet mee. */
  const middelste = (): HTMLElement | undefined => {
    const midden = rij.scrollLeft + rij.clientWidth / 2;
    let beste: HTMLElement | undefined;
    for (const vak of vakken) {
      const afstand = Math.abs(vak.offsetLeft + vak.offsetWidth / 2 - midden);
      if (!beste || afstand < Math.abs(beste.offsetLeft + beste.offsetWidth / 2 - midden)) beste = vak;
    }
    return beste;
  };
  let gepland = false;
  // Terugval voor browsers zonder scrollend: het label volgt 200 ms na het laatste scroll-event.
  let stil: ReturnType<typeof setTimeout> | undefined;
  rij.addEventListener(
    "scroll",
    () => {
      clearTimeout(stil);
      stil = setTimeout(zetLabel, 200);
      if (gepland) return;
      gepland = true;
      requestAnimationFrame(() => {
        gepland = false;
        const vak = middelste();
        // Een sprong die de bezoeker niet zelf maakte (Safari na het laden): terug naar Bouwen.
        if (!zelfBewogen && heroStart && vak !== heroStart) return centreer();
        const naam = vak?.dataset.heroVak;
        if (naam) zetActief(naam);
      });
    },
    { passive: true },
  );
  rij.addEventListener("scrollend", () => {
    clearTimeout(stil);
    const vak = middelste();
    if (!zelfBewogen && heroStart && vak !== heroStart) return centreer();
    const naam = vak?.dataset.heroVak;
    if (naam) zetActief(naam);
    zetLabel();
  });

  // scrollTo op de rij in plaats van scrollIntoView, zodat de pagina zelf niet verspringt.
  const naarMidden = (doel: HTMLElement): void => {
    zelfBewogen = true;
    rij.scrollTo({
      left: doel.offsetLeft + doel.offsetWidth / 2 - rij.clientWidth / 2,
      behavior: minderBeweging.matches ? "auto" : "smooth",
    });
  };
  for (const vak of vakken) {
    vak.addEventListener("click", () => {
      if (vak.dataset.heroVak !== actief) naarMidden(vak);
    });
  }
  for (const stip of stippen) {
    stip.addEventListener("click", () => {
      const doel = vakken.find((v) => v.dataset.heroVak === stip.dataset.heroDot);
      if (doel) naarMidden(doel);
    });
  }
}
